import { supabase, logAuditAction } from "@/lib/supabase"
import type { Payout, Wallet, BankAccount, PayoutBatch } from "@/types"

type WalletWithUser = Wallet & { user_email: string; user_name: string | null }
type BankWithUser   = BankAccount & { user_email: string; user_name: string | null }
type PayoutWithUser = Payout & { user_email: string; user_name: string | null }

export async function getWallets(filter?: {
  status?: string
  minBalance?: number
}): Promise<WalletWithUser[]> {
  let query = supabase
    .from("wallets")
    .select("*, users(email, full_name)")
    .order("balance", { ascending: false })
  if (filter?.status && filter.status !== "all") {
    query = query.eq("status", filter.status)
  }
  if (filter?.minBalance !== undefined && filter.minBalance > 0) {
    query = query.gte("balance", filter.minBalance)
  }
  const { data, error } = await query
  if (error) throw error
  return (data ?? []).map((w: Record<string, unknown>) => ({
    ...(w as unknown as Wallet),
    user_email: (w.users as Record<string, unknown>)?.email as string ?? "",
    user_name:  (w.users as Record<string, unknown>)?.full_name as string | null ?? null,
  }))
}

export async function freezeWallet(walletId: string, reason: string) {
  const { error } = await supabase
    .from("wallets")
    .update({ status: "frozen", frozen_reason: reason, frozen_at: new Date().toISOString() })
    .eq("id", walletId)
  if (error) throw error
  await logAuditAction("wallet_frozen", null, `Wallet ${walletId} frozen: ${reason}`)
}

export async function unfreezeWallet(walletId: string) {
  const { error } = await supabase
    .from("wallets")
    .update({ status: "active", frozen_reason: null, frozen_at: null })
    .eq("id", walletId)
  if (error) throw error
  await logAuditAction("wallet_frozen", null, `Wallet ${walletId} unfrozen`)
}

export async function getBankAccounts(): Promise<BankWithUser[]> {
  const { data, error } = await supabase
    .from("bank_accounts")
    .select("*, users(email, full_name)")
    .order("created_at", { ascending: false })
  if (error) throw error
  return (data ?? []).map((b: Record<string, unknown>) => ({
    ...(b as unknown as BankAccount),
    user_email: (b.users as Record<string, unknown>)?.email as string ?? "",
    user_name:  (b.users as Record<string, unknown>)?.full_name as string | null ?? null,
  }))
}

export async function verifyBankAccount(bankAccountId: string) {
  const { error } = await supabase
    .from("bank_accounts")
    .update({ status: "verified", verified_at: new Date().toISOString() })
    .eq("id", bankAccountId)
  if (error) throw error
  await logAuditAction("bank_verified", null, `Bank account ${bankAccountId} verified`)
}

export async function removeBankAccount(bankAccountId: string) {
  const { error } = await supabase
    .from("bank_accounts")
    .update({ status: "removed" })
    .eq("id", bankAccountId)
  if (error) throw error
}

export async function getW9Forms(status?: string) {
  let query = supabase
    .from("w9_forms")
    .select("*, users(email, full_name)")
    .order("created_at", { ascending: false })
  if (status && status !== "all") {
    query = query.eq("status", status)
  }
  const { data, error } = await query
  if (error) throw error
  return data ?? []
}

export async function approveW9(w9Id: string, adminId: string) {
  const { error } = await supabase
    .from("w9_forms")
    .update({ status: "approved", reviewed_by: adminId, reviewed_at: new Date().toISOString() })
    .eq("id", w9Id)
  if (error) throw error
  await logAuditAction("w9_approved", null, `W-9 ${w9Id} approved`)
}

export async function rejectW9(w9Id: string, adminId: string, reason: string) {
  const { error } = await supabase
    .from("w9_forms")
    .update({ status: "rejected", reviewed_by: adminId, reviewed_at: new Date().toISOString(), rejection_reason: reason })
    .eq("id", w9Id)
  if (error) throw error
  await logAuditAction("w9_rejected", null, `W-9 ${w9Id} rejected: ${reason}`)
}

export async function getPayouts(status?: string): Promise<PayoutWithUser[]> {
  let query = supabase
    .from("payouts")
    .select("*, users(email, full_name)")
    .order("created_at", { ascending: false })
  if (status && status !== "all") {
    query = query.eq("status", status)
  }
  const { data, error } = await query
  if (error) throw error
  return (data ?? []).map((p: Record<string, unknown>) => ({
    ...(p as unknown as Payout),
    user_email: (p.users as Record<string, unknown>)?.email as string ?? "",
    user_name:  (p.users as Record<string, unknown>)?.full_name as string | null ?? null,
  }))
}

export async function approvePayout(payoutId: string, adminId: string) {
  const { data: payout, error: fetchError } = await supabase
    .from("payouts")
    .select("*, wallets(*)")
    .eq("id", payoutId)
    .maybeSingle()
  if (fetchError) throw fetchError
  if (!payout) throw new Error("Payout not found")

  const wallet = payout.wallets as Record<string, unknown>
  const currentBalance = (wallet?.balance as number) ?? 0
  const amount = payout.amount as number

  if (currentBalance < amount) {
    throw new Error(
      `Insufficient balance: $${currentBalance.toFixed(2)} available, $${amount.toFixed(2)} requested`
    )
  }

  const { error: walletError } = await supabase
    .from("wallets")
    .update({
      balance: currentBalance - amount,
      lifetime_paid: ((wallet?.lifetime_paid as number) ?? 0) + amount,
    })
    .eq("id", payout.wallet_id)
  if (walletError) throw walletError

  const { error: payoutError } = await supabase
    .from("payouts")
    .update({ status: "processing", processed_at: new Date().toISOString() })
    .eq("id", payoutId)
  if (payoutError) throw payoutError

  await logAuditAction("payout_approved", payout.user_id as string, `Payout $${amount.toFixed(2)} approved`)
}

export async function cancelPayout(payoutId: string, reason: string) {
  const { error } = await supabase
    .from("payouts")
    .update({ status: "cancelled", failure_reason: reason })
    .eq("id", payoutId)
  if (error) throw error
  await logAuditAction("payout_cancelled", null, `Payout ${payoutId} cancelled: ${reason}`)
}

export async function getPayoutBatches(): Promise<PayoutBatch[]> {
  const { data, error } = await supabase
    .from("payout_batches")
    .select("*")
    .order("created_at", { ascending: false })
  if (error) throw error
  return (data ?? []) as PayoutBatch[]
}

export async function createPayoutBatch(
  payoutIds: string[],
  effectiveDate: string,
  adminId: string,
  companyName: string,
  companyId: string
): Promise<string> {
  const batchNumber = "BATCH-" + Date.now()
  const { data: payouts, error: fetchError } = await supabase
    .from("payouts")
    .select("*")
    .in("id", payoutIds)
    .eq("status", "processing")
  if (fetchError) throw fetchError
  const totalAmount = (payouts ?? []).reduce((sum, p) => sum + (p.amount ?? 0), 0)
  const { data: batch, error } = await supabase
    .from("payout_batches")
    .insert({
      batch_number: batchNumber,
      status: "draft",
      total_entries: payoutIds.length,
      total_amount: totalAmount,
      effective_date: effectiveDate,
      created_by: adminId,
      company_name: companyName,
      company_id: companyId,
      created_at: new Date().toISOString(),
    })
    .select()
    .single()
  if (error) throw error
  return batch.id
}

export async function approvePayoutBatch(batchId: string, adminId: string) {
  const { error } = await supabase
    .from("payout_batches")
    .update({ status: "approved", approved_by: adminId, approved_at: new Date().toISOString() })
    .eq("id", batchId)
  if (error) throw error
  await logAuditAction("batch_approved", null, `Batch ${batchId} approved`)
}

export async function markBatchSubmitted(batchId: string) {
  const { error } = await supabase
    .from("payout_batches")
    .update({ status: "submitted", submitted_at: new Date().toISOString() })
    .eq("id", batchId)
  if (error) throw error
  await logAuditAction("batch_submitted", null, `Batch ${batchId} submitted to bank`)
}